import React, { useReducer, useState, useEffect } from "react";
import {
  teamBuilderReducer,
  initialTeamBuilderState,
} from "./TeamBuilderReducer";
import ProductGameHeader from "../ProductGameHeader";
import { useTranslation } from "react-i18next";
import { Product } from "../../module/types";
import "sass/views/roleDistributor/RoleDistributorView.scss";
import { useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";

interface Props {
  product: Product;
}

/**
 * TeamBuilder 메인 게임 뷰
 * - 1인용, 서버 통신 없음
 * - 팀 개수와 총 인원 설정
 * - 랜덤 팀 배정 결과 표시
 */
const TeamBuilderView: React.FC<Props> = ({ product }) => {
  const { t, i18n } = useTranslation();
  const [state, dispatch] = useReducer(
    teamBuilderReducer,
    initialTeamBuilderState
  );
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const navigate = useNavigate();

  const { totalPlayers, teamCount, started, assignments } = state;

  // ==================== EVENT HANDLERS ====================

  const handleTotalPlayersChange = (newCount: number) => {
    dispatch({ type: "SET_TOTAL_PLAYERS", count: newCount });
  };

  const handleTeamCountChange = (newCount: number) => {
    dispatch({ type: "SET_TEAM_COUNT", count: newCount });
  };

  const handleStartTeamBuilding = () => {
    dispatch({ type: "START_TEAM_BUILDING" });
  };

  const handleReset = () => {
    dispatch({ type: "RESET" });
  };

  const handleLeaveRoom = () => {
    navigate("/", { replace: true });
  };

  // ==================== RENDER FUNCTIONS ====================

  // 헤더 렌더링
  const renderHeader = () => (
    <ProductGameHeader
      product={product}
      isRuleOpen={isRuleOpen}
      setIsRuleOpen={setIsRuleOpen}
      isSettingOpen={isSettingOpen}
      setIsSettingOpen={setIsSettingOpen}
      t={t}
    />
  );

  const renderSettingMenu = () =>
    isSettingOpen && (
      <div className="fixed right-0 top-[48px] mt-2 w-40 bg-gray-800 rounded-lg shadow-xl z-40 border border-gray-700">
        <ul className="py-1">
          <li
            className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-red-400 hover:text-white transition-colors"
            onClick={handleLeaveRoom}
          >
            <i className="fas fa-sign-out-alt mr-2" />
            {t("leaveRoom")}
          </li>
        </ul>
      </div>
    );

  // 설정 패널 렌더링
  const renderSettings = () => {
    if (started) return null;

    return (
      <div className="max-w-2xl w-full mx-auto bg-gray-800/50 rounded-xl p-8 border border-gray-600">
        <h3 className="text-2xl font-bold text-white mb-8 text-center flex items-center justify-center">
          <i className="fas fa-users text-blue-400 mr-3" />
          {t("TeamBuilder.teamAssignmentSettings")}
        </h3>

        {/* 총 인원 수 설정 */}
        <div className="space-y-4 mb-8">
          <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-700/30 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <label className="text-base font-medium text-blue-300 whitespace-nowrap">
                {t("TeamBuilder.totalPlayers")}
              </label>
              <div className="flex items-center justify-center gap-2 ml-auto">
                <button
                  onClick={() => handleTotalPlayersChange(totalPlayers - 1)}
                  disabled={totalPlayers <= 1}
                  className="px-3 py-2 text-blue-300 hover:text-white hover:bg-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-base"
                >
                  −
                </button>
                <span className="w-20 text-center text-white font-medium text-base">
                  {totalPlayers}
                  {t("TeamBuilder.people")}
                </span>
                <button
                  onClick={() => handleTotalPlayersChange(totalPlayers + 1)}
                  className="px-3 py-2 text-blue-300 hover:text-white hover:bg-blue-600/20 rounded transition-colors text-base"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 팀 개수 설정 */}
        <div className="space-y-4 mb-8">
          <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-700/30 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <label className="text-base font-medium text-green-300 whitespace-nowrap">
                {t("TeamBuilder.teamCount")}
              </label>
              <div className="flex items-center justify-center gap-2 ml-auto">
                <button
                  onClick={() => handleTeamCountChange(teamCount - 1)}
                  disabled={teamCount <= 1}
                  className="px-3 py-2 text-green-300 hover:text-white hover:bg-green-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-base"
                >
                  −
                </button>
                <span className="w-20 text-center text-white font-medium text-base">
                  {teamCount}
                  {t("TeamBuilder.teams")}
                </span>
                <button
                  onClick={() => handleTeamCountChange(teamCount + 1)}
                  disabled={teamCount >= totalPlayers}
                  className="px-3 py-2 text-green-300 hover:text-white hover:bg-green-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-base"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <div className="text-center text-gray-400 text-base">
            {t("TeamBuilder.maxTeamsCanBeSet", { count: totalPlayers })}
          </div>
        </div>

        {/* 팀 편성 시작 버튼 */}
        <div className="text-center mt-10">
          <button
            onClick={handleStartTeamBuilding}
            className="px-10 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-bold text-xl transition-colors flex items-center gap-3 mx-auto"
          >
            <i className="fas fa-play text-lg" />
            {t("TeamBuilder.startTeamAssignment")}
          </button>
        </div>
      </div>
    );
  };

  // 팀별 멤버 그룹화
  const getTeamGroups = () => {
    const teams: { [key: number]: number[] } = {};

    assignments.forEach((assignment) => {
      if (!teams[assignment.teamNumber]) {
        teams[assignment.teamNumber] = [];
      }
      teams[assignment.teamNumber].push(assignment.playerNumber);
    });

    return teams;
  };

  // 팀별 색상 결정
  const getTeamColor = (teamNumber: number) => {
    const colors = [
      {
        bg: "bg-blue-900/20 border-blue-700/30",
        badge: "bg-blue-600 text-blue-50",
      },
      {
        bg: "bg-red-900/20 border-red-700/30",
        badge: "bg-red-600 text-red-50",
      },
      {
        bg: "bg-green-900/20 border-green-700/30",
        badge: "bg-green-600 text-green-50",
      },
      {
        bg: "bg-purple-900/20 border-purple-700/30",
        badge: "bg-purple-600 text-purple-50",
      },
      {
        bg: "bg-yellow-900/20 border-yellow-700/30",
        badge: "bg-yellow-600 text-yellow-50",
      },
      {
        bg: "bg-pink-900/20 border-pink-700/30",
        badge: "bg-pink-600 text-pink-50",
      },
      {
        bg: "bg-indigo-900/20 border-indigo-700/30",
        badge: "bg-indigo-600 text-indigo-50",
      },
      {
        bg: "bg-teal-900/20 border-teal-700/30",
        badge: "bg-teal-600 text-teal-50",
      },
    ];

    return colors[(teamNumber - 1) % colors.length];
  };

  // 결과 표시
  const renderResults = () => {
    if (!started) return null;

    const teams = getTeamGroups();
    const teamNumbers = Object.keys(teams)
      .map(Number)
      .sort((a, b) => a - b);

    return (
      <div className="max-w-6xl w-full mx-auto p-4">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-white mb-2 flex items-center justify-center">
            <i className="fas fa-users text-green-400 mr-3" />
            {t("TeamBuilder.teamAssignmentResults")}
          </h3>
          <div className="text-gray-400 text-sm">
            {t("TeamBuilder.totalPlayersAssignedMessage", {
              totalPlayers,
              teamCount,
            })}
          </div>
        </div>

        {/* 팀별 결과 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {teamNumbers.map((teamNumber) => {
            const teamMembers = teams[teamNumber];
            const colors = getTeamColor(teamNumber);

            return (
              <div
                key={teamNumber}
                className={`${colors.bg} border rounded-lg p-4`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-white">
                    {t("TeamBuilder.team")} {teamNumber}
                  </h4>
                  <span
                    className={`px-2 py-1 ${colors.badge} rounded text-xs font-medium`}
                  >
                    {teamMembers.length}
                    {t("TeamBuilder.peopleUnit")}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {teamMembers.map((playerNumber) => (
                    <div
                      key={playerNumber}
                      className="bg-gray-700 text-white text-center py-2 rounded text-sm font-medium"
                    >
                      {playerNumber}
                      {t("TeamBuilder.playerNumberUnit")}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* 전체 목록 (플레이어 번호 순) */}
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-600">
          <h4 className="text-lg font-bold text-white mb-4 flex items-center">
            <i className="fas fa-list text-purple-400 mr-2" />
            {t("TeamBuilder.fullListPlayerOrder")}
          </h4>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
            {assignments.map((assignment) => {
              const colors = getTeamColor(assignment.teamNumber);
              return (
                <div
                  key={assignment.playerNumber}
                  className={`flex items-center justify-between px-3 py-2 ${colors.bg} border rounded`}
                >
                  <span className="text-white font-medium text-sm">
                    {assignment.playerNumber}
                    {t("TeamBuilder.playerNumberUnit")}
                  </span>
                  <span
                    className={`px-1.5 py-0.5 ${colors.badge} rounded text-xs font-medium`}
                  >
                    {t("TeamBuilder.team")} {assignment.teamNumber}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 다시 편성 버튼 */}
        <div className="text-center mt-6">
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto text-sm"
          >
            <i className="fas fa-redo text-xs" />
            {t("TeamBuilder.reassignTeams")}
          </button>
        </div>
      </div>
    );
  };

  // 메인 컨텐츠 렌더링
  const renderMainContent = () => (
    <div className="team-builder-main flex-1 flex flex-col min-h-0 pt-[48px] overflow-y-auto">
      <div className="flex-1 flex items-start justify-center pt-4 px-4">
        {started ? renderResults() : renderSettings()}
      </div>
    </div>
  );

  // ==================== MAIN RENDER ====================

  return (
    <>
      <SEO
        title={`${product.title} - Lingple`}
        description={
          i18n.language === "ko"
            ? "팀 편성을 간단히 해보세요."
            : "Easily divide players into teams."
        }
        keywords={
          i18n.language === "ko"
            ? "팀 빌더, 팀 분배, 랜덤 팀"
            : "team builder, team distribution, random teams"
        }
      />
      <div className="space-gray-theme team-builder-container flex flex-col h-screen bg-gray-900">
        {renderHeader()}
        {renderSettingMenu()}
        {renderMainContent()}
      </div>
    </>
  );
};

export default TeamBuilderView;
